import './Tournament.css'
import { useState, useEffect } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import SmoothCollapse from 'react-smooth-collapse'
import { useDispatch, useSelector } from 'react-redux'
import { useCallback } from 'react'

import { useGetTournamentQuery } from '../../redux/singleTournamentService/singleTournamentApiSlice'
import { useGetCommunityQuery } from '../../redux/communityService/communityApiSlice'
import { setActiveComponent, setTournamentData } from '../../redux/singleTournamentService/singleTournamentSlice'
import { Header } from '../../components/Header/Header.js'
import { TournamentParticipants } from '../../components/TournamentParticipants/TournamentParticipants'
import { TournamentInfo } from '../../components/TournamentInfo/TournamentInfo'
import { TournamentStructure } from '../../components/TournamentStructure/TournamentStructure'
import { TournamentStanding } from '../../components/TournamentStanding/TournamentStanding'
import { TournamentSchedule } from '../../components/TournamentSchedule/TournamentSchedule'
import { TournamentResult } from '../../components/TournamentResult/TournamentResult'
import PlayersGrey from '../../icon/players-grey.svg'
import PlayersBlue from '../../icon/players-blue.svg'
import StructureGrey from '../../icon/structure_menu.svg'
import StructureBlue from '../../icon/structure_menu_blue.svg'
import StandingGrey from '../../icon/grids_menu.svg'
import StandingBlue from '../../icon/grids_menu_blue.svg'
import ScheduleGrey from '../../icon/sched-grey.svg'
import ScheduleBlue from '../../icon/sched-blue.svg'
import { ReactComponent as ExitGrey } from '../../icon/exit-grey.svg'
import { ReactComponent as ExitBlue } from '../../icon/exit-blue.svg'
import { ReactComponent as InfoGrey } from '../../icon/info-grey.svg'
import { ReactComponent as InfoBlue } from '../../icon/info-blue.svg'
import { ReactComponent as ArrowDown } from '../../icon/arrow-down.svg'
import { ReactComponent as CupGrey } from '../../icon/cup.svg'
import { ReactComponent as CupBlue } from '../../icon/cup_blue.svg'
import NoContent from '../../components/NoContent/NoContent'
import GlobalStore from '../../GlobalStore'

import {
	formPlayersNamesInTournament,
	checkTournamentContent,
	setCategoryOfTournament
} from './externalFunctions'

const Tournament = () => {
	const navigate = useNavigate()
	const location = useLocation()
	const dispatch = useDispatch()
	const STAGE_ORDER_KEY = sessionStorage.getItem('stageOrder')
	const MENU_TAB_NUMBER_KEY = sessionStorage.getItem('menuTabNumber')
	const { tournamentUid, guest } = useParams()
	const activeComponent = useSelector(state => state.tournament.active)
	const { authorized, communityUid } = GlobalStore.getStore()

	const [tournamentCategory, setTournamentCategory] = useState('')
	const [menuTabOrder, setMenuTabOrder] = useState(
		MENU_TAB_NUMBER_KEY && MENU_TAB_NUMBER_KEY !== '7' ? JSON.parse(MENU_TAB_NUMBER_KEY) : 1
	)
	const [stages, setStages] = useState([])
	const [stageTabOrder, setStageTabOrder] = useState(STAGE_ORDER_KEY ? JSON.parse(STAGE_ORDER_KEY) : 1)
	const [shownStage, setShownStage] = useState(menuTabOrder < 4 ? true : false)
	const [showMainMenu, setShowMainMenu] = useState(true)
	const [showNoContent, setShowNoContent] = useState(false)
	const [auth, setAuth] = useState(false)
	const [guestUrl, setGuestUrl] = useState('')
	const [matchIsActive, setMatchIsActive] = useState(false)

	console.log(showMainMenu, menuTabOrder, shownStage, stageTabOrder, activeComponent, stages)

	useEffect(() => {
		if (!guest && authorized) {
			setAuth(true)
		} else {
			setAuth(false)
		}

		if (guest) {
			GlobalStore.setItem('guestCode', guest)
		} else if (!guest) {
			GlobalStore.removeItem('guestCode')
		}
	}, [guest, authorized])

	const {
		data: tournament = {},
		error,
		isLoading,
		refetch
	} = useGetTournamentQuery({ tournament_uid: tournamentUid }, { skip: !authorized && !guest })

	const { data: community = {} } = useGetCommunityQuery(
		{ community_uid: communityUid },
		{ skip: Object.keys(tournament).length === 0 || guest || !authorized || !communityUid }
	)

	useEffect(() => {
		if (Object.keys(tournament).length !== 0) {
			setGuestUrl(`${window.location.origin}/${tournamentUid}/g/${tournament?.auth_code}`)

			setShowNoContent(() => checkTournamentContent(tournament, menuTabOrder))
			const tournamentWithFormedPlayersNames = formPlayersNamesInTournament(tournament)
			dispatch(setTournamentData(tournamentWithFormedPlayersNames))
			setStages(() => Array.from({ length: tournament?.stages_count }, (_, index) => index + 1))

			if (STAGE_ORDER_KEY) {
				setStageTabOrder(JSON.parse(STAGE_ORDER_KEY))
			} else {
				setStageTabOrder(1)
			}
		}
	}, [tournament, STAGE_ORDER_KEY, dispatch, menuTabOrder, tournamentUid])

	useEffect(() => {
		if (community && Object.keys(community).length !== 0) {
			setTournamentCategory(() => setCategoryOfTournament(community, tournament))
		} else {
			setTournamentCategory(tournament?.category?.name)
		}
	}, [community, tournament])

	const handleAddStage = useCallback(() => {
		dispatch(setActiveComponent('createStage'))
		sessionStorage.setItem('activatedComponent', 'createStage')
	}, [dispatch])

	const handleMenuTab = useCallback((number) => {
		if (activeComponent !== '') {
			dispatch(setActiveComponent(''))
			sessionStorage.removeItem('activatedComponent')
		}

		const isTournamentContent = checkTournamentContent(tournament, number)
		setShowNoContent(isTournamentContent)

		setMenuTabOrder(number)
		sessionStorage.setItem('menuTabNumber', number)

		if (number < 4) {
			setShownStage(true)
		} else if (number === 7) {
			navigate(auth ? '/tournaments' : '/')
		} else {
			setShownStage(false)
		}
	}, [activeComponent, tournament, dispatch, navigate, auth])

	const handleStageTab = useCallback((number) => {
		setStageTabOrder(number)
		dispatch(setActiveComponent(''))
		sessionStorage.setItem('stageOrder', number)
		sessionStorage.removeItem('activatedComponent')
	}, [dispatch])

	const tournamentRefetch = () => {
		refetch()
	}

	if (error) { console.log(error) }
	if (isLoading) { return <h1>Loading</h1> }

	return (
		<>
			<Header
				className="header-container"
				location={location}
				title={`${tournament?.name} ${tournamentCategory ? `(${tournamentCategory})` : ''}`}
			/>

			<div className="tournament-wrapper">
				<div className="tournament-container">
					<div className="tournament__components">
						{
							menuTabOrder === 1 && stages?.length > 0 && !showNoContent &&
							<TournamentStanding
								stageNumber={stageTabOrder}
								auth={auth}
								match={setMatchIsActive}
								menuTab={menuTabOrder} />
						}

						{
							menuTabOrder === 2 && stages?.length > 0 && !showNoContent &&
							<TournamentSchedule
								stageNumber={stageTabOrder}
								auth={auth}
								match={setMatchIsActive}
								menuTab={menuTabOrder}
							/>
						}

						{
							menuTabOrder === 3 && !showNoContent &&
							<TournamentStructure
								stageNumber={stageTabOrder}
								tournament={tournament}
								auth={auth}
								tournamentRefetch={tournamentRefetch}
								menuTab={menuTabOrder}
							/>
						}

						{
							menuTabOrder < 4 && showNoContent &&
							<NoContent menuTabOrder={menuTabOrder} tournament={tournament} auth={auth} />
						}

						{
							menuTabOrder === 4 &&
							<TournamentParticipants
								tournament={tournament}
								auth={auth}
								tournamentRefetch={tournamentRefetch}
							/>
						}

						{
							menuTabOrder === 5 && !activeComponent &&
							<TournamentInfo
								tournament={tournament}
								auth={auth}
								community={community}
								guestUrl={guestUrl}
							/>
						}

						{
							menuTabOrder === 6 && !activeComponent &&
							<TournamentResult
								tournament={tournament}
								community={community}
							/>
						}
					</div>
				</div>

				{
					!matchIsActive &&
					<div className="tournament-footer__container">
						{
							shownStage &&
							<div className="tournament__stages-menu">
								{
									stages?.length > 0 &&
									<div className="stages-menu__item">
										{
											menuTabOrder === 1 &&
											<img
												src={StandingBlue}
												className="stages-menu__item-icon"
												alt="Standing"
											/>
										}

										{
											menuTabOrder === 2 &&
											<img
												src={ScheduleBlue}
												className="stages-menu__item-icon"
												alt="Schedule"
											/>
										}

										{
											menuTabOrder === 3 &&
											<img
												src={StructureBlue}
												className="stages-menu__item-icon"
												alt="Structure"
											/>
										}

										{
											stages?.map((s, index) => (
												<button
													type="button"
													key={index}
													onClick={() => handleStageTab(index + 1)}
													className={
														stageTabOrder === index + 1 &&
															!activeComponent ?
															'tournament__stages-tab--chosen'
															:
															'tournament__stages-tab'
													}
												>
													{index + 1}
												</button>
											))
										}

										{
											menuTabOrder === 3 && auth && stages?.length < 2 &&
											<button
												type="button"
												onClick={handleAddStage}
												className={
													activeComponent === 'createStage' ?
														'tournament__stage-addition--active'
														:
														'button-stage-addition'
												}
											>
												+
											</button>
										}

										<ArrowDown
											className={showMainMenu ?
												'arrow--down'
												:
												'arrow--up'
											}
											onClick={() => setShowMainMenu(prev => !prev)}
										/>
									</div>
								}

								{
									stages?.length === 0 && menuTabOrder === 3 && auth &&
									<div className="tournament__stage-menu">
										<img
											src={StructureBlue}
											className="stages-menu__item-icon"
											alt="Structure"
										/>

										<button
											type="button"
											onClick={handleAddStage}
											className="tournament__stage-addition--active"
										>
											+
										</button>

										<ArrowDown
											className={showMainMenu ?
												'arrow--down'
												:
												'arrow--up'
											}
											onClick={() => setShowMainMenu(prev => !prev)}
										/>
									</div>
								}
							</div>
						}

						<SmoothCollapse expanded={showMainMenu}>
							<div className={menuTabOrder > 3 || (menuTabOrder < 3 && stages?.length < 1) ?
								'tournament__footer-menu'
								:
								'tournament__footer-menu--opened'
							}>
								<div onClick={() => handleMenuTab(1)} className={'tournament-footer__link'}>
									{
										menuTabOrder === 1 ?
											<img
												src={StandingBlue}
												style={{ height: '27px', width: '33px' }}
												alt="Standing"
											/>
											:
											<img
												src={StandingGrey}
												style={{ height: '27px', width: '33px' }}
												alt="Standing"
											/>
									}
								</div>

								<div onClick={() => handleMenuTab(2)} className={'tournament-footer__link'}>
									{
										menuTabOrder === 2 ?
											<img
												src={ScheduleBlue}
												style={{ height: '32px', width: '31px' }}
												alt="Schedule"
											/>
											:
											<img
												src={ScheduleGrey}
												style={{ height: '32px', width: '31px' }}
												alt="Schedule"
											/>
									}
								</div>

								{!guest &&
									<div onClick={() => handleMenuTab(3)} className={'tournament-footer__link'}>
										{
											menuTabOrder === 3 ?
												<img
													src={StructureBlue}
													style={{ height: '30px', width: '38px' }}
													alt="Structure"
												/>
												:
												<img
													src={StructureGrey}
													style={{ height: '30px', width: '38px' }}
													alt="Structure"
												/>
										}
									</div>
								}

								<div onClick={() => handleMenuTab(4)} className={'tournament-footer__link'}>
									{
										menuTabOrder === 4 ?
											<img
												src={PlayersBlue}
												style={{ height: '30px', width: '30px' }}
												alt="Players"
											/>
											:
											<img
												src={PlayersGrey}
												style={{ height: '30px', width: '30px' }}
												alt="Players"
											/>
									}
								</div>

								<div onClick={() => handleMenuTab(6)} className={'tournament-footer__link'}>
									{
										menuTabOrder === 6 ?
											<CupBlue className="cup-icon" />
											:
											<CupGrey className="cup-icon" />
									}
								</div>

								<div onClick={() => handleMenuTab(5)} className={'tournament-footer__link'}>
									{
										menuTabOrder === 5 ?
											<InfoBlue className="footer-icon" />
											:
											<InfoGrey className="footer-icon" />
									}
								</div>

								<div onClick={() => handleMenuTab(7)} className={'tournament-footer__link'}>
									{
										menuTabOrder === 7 ?
											<ExitBlue className="footer-icon" />
											:
											<ExitGrey className="footer-icon" />
									}
								</div>
							</div>
						</SmoothCollapse>
					</div>
				}
			</div>
		</>
	)
}

export default Tournament